import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import { getTwilioDeviceInstance, startSipCall } from '../../../services/twilio/twilioService';
import { fetchWithAuth } from '../../../api/backendApi';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  listBrowserAudioDevices,
  loadPhoneAudioSettings,
  requestAudioPermission,
  savePhoneAudioSettings,
} from '../../../utils/browserAudioDevices';

export default function SipPagingDialog({ open, onClose, presetZoneId, presetZoneName, sipUri }) {
  const { tenantId } = useAuth();
  const [zoneId, setZoneId] = useState('');
  const [isPaging, setIsPaging] = useState(false);
  const [status, setStatus] = useState('Idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [micDeviceId, setMicDeviceId] = useState(() => loadPhoneAudioSettings().micDeviceId);
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const deviceRef = useRef(null);
  const connectionRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (presetZoneId) {
      setZoneId(presetZoneId);
    } else if (!open) {
      setZoneId('');
    }
  }, [presetZoneId, open]);

  useEffect(() => {
    if (!open) {
      setErrorMessage('');
      setErrorDetails('');
      setShowErrorDetails(false);
    }
  }, [open]);

  const refreshMicrophones = useCallback(async () => {
    setAudioLoading(true);
    try {
      const allowed = await requestAudioPermission();
      const { inputs } = await listBrowserAudioDevices();
      setAudioInputs(inputs);
      if (!allowed && inputs.length === 0) {
        setErrorMessage('Microphone access is blocked. Allow the microphone for this site, then refresh the list.');
      }
    } catch (err) {
      console.warn('[SipPagingDialog] microphone list failed', err);
      setAudioInputs([]);
    } finally {
      setAudioLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMicDeviceId(loadPhoneAudioSettings().micDeviceId);
    refreshMicrophones();
    const media = navigator.mediaDevices;
    if (media?.addEventListener) {
      media.addEventListener('devicechange', refreshMicrophones);
    }
    return () => {
      media?.removeEventListener?.('devicechange', refreshMicrophones);
    };
  }, [open, refreshMicrophones]);

  const handleMicChange = (event) => {
    const nextId = event.target.value;
    setMicDeviceId(nextId);
    savePhoneAudioSettings({ micDeviceId: nextId });
    setErrorMessage('');
  };

  const formatSipPagingError = (code, details) => {
    const codeStr = typeof code === 'string' ? code : '';
    const detailsText = details ? JSON.stringify(details) : '';

    if (codeStr === 'sip_uri_missing') {
      return {
        message:
          'SIP paging is not set up for this zone yet. Ask your administrator to configure the SIP endpoint (SIP URI).',
        details: detailsText,
      };
    }

    if (codeStr === 'sip_domain_not_configured') {
      return {
        message:
          'SIP paging is not set up for this tenant yet. Ask your administrator to configure the SIP endpoint (SIP domain).',
        details: detailsText,
      };
    }

    if (codeStr === 'cross_tenant_not_allowed') {
      return {
        message:
          'SIP paging is not set up for this tenant. Ask your administrator to configure the SIP endpoint so it matches this tenant.',
        details: detailsText,
      };
    }

    if (codeStr === 'sip_paging_disabled') {
      return {
        message: 'SIP paging is not enabled for this tenant. Ask your administrator to enable SIP paging.',
        details: detailsText,
      };
    }

    if (codeStr === 'zone_access_denied' || codeStr === 'tenant_access_denied') {
      return {
        message: 'You do not have access to SIP paging for this zone.',
        details: detailsText,
      };
    }

    if (codeStr === 'zone_not_found') {
      return {
        message: 'SIP paging zone not found or not accessible.',
        details: detailsText,
      };
    }

    if (codeStr === 'zone_inactive') {
      return {
        message: 'This SIP paging zone is inactive.',
        details: detailsText,
      };
    }

    if (codeStr) {
      return {
        message: 'SIP paging could not be started. Please contact your administrator.',
        details: JSON.stringify({ error: codeStr, details }),
      };
    }

    return {
      message: 'SIP paging could not be started. Please try again or contact your administrator.',
      details: detailsText,
    };
  };

  const parseApiErrorFromMessage = (msg) => {
    const text = typeof msg === 'string' ? msg : '';
    const match = text.match(/^API Error \((\d+)\):\s*(.*)$/s);
    if (!match) return null;
    const httpStatus = Number(match[1]);
    const bodyText = match[2] || '';
    try {
      const parsed = JSON.parse(bodyText);
      return { httpStatus, bodyText, parsed };
    } catch (_) {
      return { httpStatus, bodyText, parsed: null };
    }
  };

  const presentError = (err, fallback) => {
    const apiErr = parseApiErrorFromMessage(err?.message);
    if (apiErr?.parsed && typeof apiErr.parsed === 'object') {
      const { error, details } = apiErr.parsed;
      const formatted = formatSipPagingError(error, details);
      setErrorMessage(formatted.message);
      setErrorDetails(formatted.details || JSON.stringify(apiErr.parsed));
      setShowErrorDetails(false);
      setStatus(formatted.message);
      return;
    }

    const msg = err?.message || fallback || 'SIP paging could not be started.';
    setErrorMessage(fallback || msg);
    setErrorDetails(msg);
    setShowErrorDetails(false);
    setStatus(fallback || msg);
  };

  useEffect(() => {
    if (isPaging) {
      const start = Date.now();
      setElapsedMs(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - start);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaging]);

  const attachCallHandlers = (call) => {
    if (!call) {
      setIsConnecting(false);
      return;
    }
    connectionRef.current = call;
    setIsConnecting(false);
    setIsPaging(true);
    setStatus('Live - your microphone is broadcasting');

    try {
      call.on('disconnect', () => {
        console.log('[SipPagingDialog] SIP call disconnected (call handler)');
        connectionRef.current = null;
        setIsPaging(false);
        setStatus('Disconnected');
      });

      call.on('error', (error) => {
        console.error('CALL ERROR FULL', {
          code: error?.code,
          message: error?.message,
          raw: error,
        });
        presentError(error, 'SIP paging call error.');
        setIsPaging(false);
      });
    } catch (e) {
      console.error('[SipPagingDialog] Error attaching call handlers', e);
    }
  };

  const handleStartStop = async () => {
    if (isConnecting) {
      // Cancel connecting
      if (deviceRef.current) {
        try {
          deviceRef.current.disconnectAll();
        } catch (e) {
          console.error('[SipPagingDialog] Error canceling connection', e);
        }
      }
      setIsConnecting(false);
      setStatus('Cancelled');
      return;
    }

    if (!isPaging) {
      // Start paging
      if (!presetZoneId && !zoneId.trim()) {
        setStatus('A paging zone is required');
        toast.error('A paging zone is required');
        return;
      }

      try {
        setErrorMessage('');
        setErrorDetails('');
        setShowErrorDetails(false);
        setIsConnecting(true);
        setStatus('Initializing voice client...');
        console.log('[SipPagingDialog] Starting SIP paging');

        setStatus('Authorizing SIP paging...');
        const zoneIdToUse = (presetZoneId || zoneId).trim();

        if (!tenantId) {
          toast.error('Tenant not available');
          setStatus('Tenant not available');
          setIsConnecting(false);
          return;
        }

        const authorizeEndpoint = `/api/v2/tenants/${tenantId}/sip-paging/authorize`;
        const authorizeResp = await fetchWithAuth(
          authorizeEndpoint,
          'POST',
          { zoneId: zoneIdToUse }
        );

        if (!authorizeResp?.success || !authorizeResp?.authToken) {
          console.error('[SipPagingDialog] SIP authorize failed', authorizeResp);
          const formatted = formatSipPagingError(authorizeResp?.error, authorizeResp?.details);
          toast.error(formatted.message);
          setErrorMessage(formatted.message);
          setErrorDetails(formatted.details || JSON.stringify(authorizeResp || {}));
          setShowErrorDetails(false);
          setStatus(formatted.message);
          setIsConnecting(false);
          return;
        }
        const { authToken } = authorizeResp;

        setStatus('Connecting to SIP endpoint...');
        const result = startSipCall({
          authToken,
          micDeviceId,
        });

        if (result && typeof result.then === 'function') {
          result
            .then((call) => {
              deviceRef.current = getTwilioDeviceInstance();
              attachCallHandlers(call);
            })
            .catch((error) => {
              console.error('CALL ERROR FULL', {
                code: error?.code,
                message: error?.message,
                raw: error,
              });
              toast.error('Failed to start SIP paging');
              presentError(error, 'Failed to start SIP paging');
              setIsConnecting(false);
            });
        } else {
          attachCallHandlers(result);
        }
      } catch (e) {
        console.error('[SipPagingDialog] Error starting SIP paging', e);
        toast.error('Failed to start SIP paging');
        presentError(e, 'Failed to start SIP paging');
        setIsConnecting(false);
      }
    } else {
      // Stop paging
      console.log('[SipPagingDialog] Stopping SIP paging');
      if (connectionRef.current) {
        try {
          connectionRef.current.disconnect();
        } catch (e) {
          console.error('[SipPagingDialog] Error disconnecting call', e);
        }
        connectionRef.current = null;
      }
      if (deviceRef.current) {
        try {
          deviceRef.current.disconnectAll();
        } catch (e) {
          console.error('[SipPagingDialog] Error disconnecting device', e);
        }
      }
      setIsPaging(false);
      setStatus('Stopped');
      toast.info('SIP paging stopped');
    }
  };

  const handleClose = () => {
    // Clean up any active connection
    if (connectionRef.current) {
      try {
        connectionRef.current.disconnect();
      } catch (e) {
        console.error('[SipPagingDialog] Error disconnecting on close', e);
      }
      connectionRef.current = null;
    }
    if (deviceRef.current) {
      try {
        deviceRef.current.disconnectAll();
      } catch (e) {
        console.error('[SipPagingDialog] Error disconnecting device on close', e);
      }
    }
    setIsPaging(false);
    setIsConnecting(false);
    setStatus('Idle');
    setErrorMessage('');
    setErrorDetails('');
    setShowErrorDetails(false);
    setElapsedMs(0);
    onClose?.();
  };

  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const timerLabel = `${minutes}:${seconds}`;

  const showStop = isPaging || isConnecting;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">Live Paging (SIP)</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {presetZoneId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Zone
              </Typography>
              <Typography variant="body2" color="text.primary">
                {presetZoneName || 'Paging zone'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                SIP paging zone configured by your administrator
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No paging zone is set on this button.
            </Typography>
          )}

          {!!errorMessage && (
            <Box>
              <Alert
                severity="error"
                action={
                  errorDetails ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => setShowErrorDetails((v) => !v)}
                    >
                      {showErrorDetails ? 'Hide details' : 'Details'}
                    </Button>
                  ) : null
                }
              >
                {errorMessage}
              </Alert>
              {errorDetails && (
                <Collapse in={showErrorDetails}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                      {errorDetails}
                    </Typography>
                  </Box>
                </Collapse>
              )}
            </Box>
          )}

          <Typography variant="body2" color="text.secondary">
            {isPaging
              ? 'Tap the mic to stop paging. Your microphone is live to this zone.'
              : isConnecting
              ? 'Connecting... Tap to cancel.'
              : 'Choose a microphone, then tap the mic to start paging.'}
          </Typography>

          <FormControl fullWidth size="small" disabled={isPaging || isConnecting}>
            <InputLabel id="sip-paging-mic-label">Microphone</InputLabel>
            <Select
              labelId="sip-paging-mic-label"
              label="Microphone"
              value={micDeviceId || ''}
              onChange={handleMicChange}
              renderValue={(val) => {
                if (!val) return 'Default microphone';
                const found = audioInputs.find((d) => d.deviceId === val);
                return found?.label || 'Selected microphone';
              }}
            >
              <MenuItem value="">Default microphone</MenuItem>
              {micDeviceId && !audioInputs.some((d) => d.deviceId === micDeviceId) && (
                <MenuItem value={micDeviceId}>Saved microphone</MenuItem>
              )}
              {audioInputs.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label || 'Microphone'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {audioLoading && (
            <Typography variant="caption" color="text.secondary">Loading microphones…</Typography>
          )}
          {!audioLoading && audioInputs.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No microphones found. Allow microphone access, then refresh the list.
            </Typography>
          )}
          <Button size="small" onClick={refreshMicrophones} disabled={isPaging || isConnecting || audioLoading}>
            Refresh microphones
          </Button>

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleStartStop}
              color={showStop ? 'error' : 'primary'}
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                boxShadow: 4,
              }}
            >
              {showStop ? <StopIcon sx={{ fontSize: 40 }} /> : <MicIcon sx={{ fontSize: 40 }} />}
            </IconButton>
            <Typography
              variant="h6"
              sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}
            >
              {timerLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {status}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
